import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Badge,
  Button,
  Dropdown,
  Field,
  Input,
  Option,
  Switch,
  Text,
  Textarea,
  Tooltip,
  makeStyles,
  mergeClasses,
  tokens,
} from "@coreai/fluentui-react";
import {
  AddRegular,
  ChevronDown20Regular,
  ChevronRight20Regular,
  DeleteRegular,
  ErrorCircle16Regular,
} from "@fluentui/react-icons";
import {
  PARAMETER_TYPES,
  findDuplicateNames,
  fromRaw,
  labelOf,
  makeRowId,
  nextDefaultName,
  rowsFromJson,
  rowsToJson,
  toRaw,
  validateRows,
  validateValue,
  withAdded,
  withRemoved,
  withToggled,
  type ParameterType,
  type Row,
} from "@/utils/parameters";

export interface ParametersFormEditorProps {
  /** Source-of-truth JSON string from the parent editor state. */
  value: string;
  /** Emit a new JSON string back to the parent's `handleJsonChange`. */
  onChange: (json: string) => void;
  /**
   * Reports the form's structural validity (duplicate / empty names) so the
   * parent can block Save. Not cleared on unmount — the parent owns lifecycle
   * because the lossy rawJson outlives this view.
   */
  onValidityChange?: (error: string | null) => void;
  readOnly?: boolean;
}

type ValidationProps =
  | { validationState: "error"; validationMessage: string }
  | Record<string, never>;

const validationProps = (message: string | undefined): ValidationProps =>
  message ? { validationState: "error", validationMessage: message } : {};

const displayNameOf = (row: Row) => row.name.trim() || "Unnamed parameter";

/**
 * Form-based view of `parameters.json`. Renders one collapsible card per
 * parameter; the parent's JSON string remains the single source of truth so
 * Save/Discard/draft-mode work identically across this and the JSON editor.
 */
export const ParametersFormEditor = ({
  value,
  onChange,
  onValidityChange,
  readOnly = false,
}: ParametersFormEditorProps) => {
  const styles = useStyles();

  // Local row state mirrors `value` but is held separately so the user's
  // keystrokes don't lose focus when the parent re-serializes JSON.
  const initial = useRef(rowsFromJson(value)).current;
  const [rows, setRows] = useState<Row[]>(initial.rows);
  const [parseError, setParseError] = useState(!initial.ok);
  const [expandedIds, setExpandedIds] = useState<ReadonlySet<string>>(
    () => new Set(),
  );

  // Track the JSON we last emitted so we can distinguish our own writes from
  // external resets (Save persisted, Discard, refetch, draft-mode toggle).
  const lastEmittedRef = useRef<string>(rowsToJson(initial.rows));

  useEffect(() => {
    if (value === lastEmittedRef.current) return;
    const parsed = rowsFromJson(value);
    setParseError(!parsed.ok);
    if (parsed.ok) {
      setRows(parsed.rows);
      lastEmittedRef.current = rowsToJson(parsed.rows);
    }
  }, [value]);

  const mutateRows = useCallback(
    (produce: (prev: Row[]) => Row[]) => {
      setRows((prev) => {
        const next = produce(prev);
        if (next === prev) return prev;
        const json = rowsToJson(next);
        lastEmittedRef.current = json;
        onChange(json);
        return next;
      });
    },
    [onChange],
  );

  const duplicateNames = useMemo(() => findDuplicateNames(rows), [rows]);

  // Surface structural errors (duplicate / empty names) to the parent so it
  // can disable Save — a JSON object can't carry duplicate keys, so the
  // emitted payload would silently drop entries on save. Intentionally not
  // cleared on unmount: rowsToJson is last-write-wins for dupes, so the lossy
  // rawJson outlives this view. The parent clears formError when the JSON is
  // explicitly replaced or discarded.
  useEffect(() => {
    onValidityChange?.(parseError ? null : validateRows(rows));
  }, [rows, parseError, onValidityChange]);

  const updateRow = useCallback(
    (id: string, patch: Partial<Row>) => {
      mutateRows((prev) =>
        prev.map((r) => {
          if (r.id !== id) return r;
          const merged: Row = { ...r, ...patch };
          // On a type change, reformat the existing raw value through the new
          // type's coercion so e.g. switching to Bool snaps to true/false.
          if (patch.type && patch.type !== r.type) {
            merged.rawValue = toRaw(patch.type, fromRaw(r.type, r.rawValue));
          }
          return merged;
        }),
      );
    },
    [mutateRows],
  );

  const addRow = useCallback(() => {
    const newId = makeRowId();
    mutateRows((prev) => [
      ...prev,
      {
        id: newId,
        name: nextDefaultName(prev.map((r) => r.name)),
        type: "String",
        rawValue: "",
        description: "",
      },
    ]);
    // Auto-expand the new row so the user can fill it in immediately.
    setExpandedIds((ids) => withAdded(ids, newId));
  }, [mutateRows]);

  const removeRow = useCallback(
    (id: string) => {
      mutateRows((prev) => prev.filter((r) => r.id !== id));
      setExpandedIds((ids) => withRemoved(ids, id));
    },
    [mutateRows],
  );

  const toggleExpanded = useCallback((id: string) => {
    setExpandedIds((ids) => withToggled(ids, id));
  }, []);

  if (parseError) {
    return (
      <div className={styles.root}>
        <div className={styles.banner}>
          The JSON is not currently valid, so the form view can't be shown.
          Switch to the JSON editor to fix the syntax, then come back here.
        </div>
      </div>
    );
  }

  if (rows.length === 0) {
    return (
      <div className={styles.root}>
        <div className={styles.empty}>
          <Text>No parameters defined.</Text>
          {!readOnly && (
            <Button appearance="primary" icon={<AddRegular />} onClick={addRow}>
              Add parameter
            </Button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className={styles.root}>
      <div className={styles.list}>
        {rows.map((row) => (
          <ParameterCard
            key={row.id}
            row={row}
            expanded={expandedIds.has(row.id)}
            nameError={nameErrorFor(row, duplicateNames)}
            readOnly={readOnly}
            onToggle={toggleExpanded}
            onUpdate={updateRow}
            onRemove={removeRow}
          />
        ))}
      </div>
      {!readOnly && (
        <div className={styles.toolbar}>
          <Button appearance="secondary" icon={<AddRegular />} onClick={addRow}>
            Add parameter
          </Button>
        </div>
      )}
    </div>
  );
};

ParametersFormEditor.displayName = "ParametersFormEditor";

const nameErrorFor = (
  row: Row,
  duplicates: ReadonlySet<string>,
): string | undefined => {
  const trimmed = row.name.trim();
  if (!trimmed) return "Name is required.";
  if (duplicates.has(trimmed)) return "Duplicate parameter name.";
  return undefined;
};

interface ParameterCardProps {
  row: Row;
  expanded: boolean;
  nameError: string | undefined;
  readOnly: boolean;
  onToggle: (id: string) => void;
  onUpdate: (id: string, patch: Partial<Row>) => void;
  onRemove: (id: string) => void;
}

const ParameterCard = ({
  row,
  expanded,
  nameError,
  readOnly,
  onToggle,
  onUpdate,
  onRemove,
}: ParameterCardProps) => {
  const styles = useStyles();
  const bodyId = `${row.id}-body`;
  const displayName = displayNameOf(row);

  return (
    <div className={mergeClasses(styles.card, expanded && styles.cardExpanded)}>
      <div className={styles.summaryRow}>
        <button
          type="button"
          className={styles.summary}
          onClick={() => onToggle(row.id)}
          aria-expanded={expanded}
          aria-controls={bodyId}
        >
          <span className={styles.summaryChevron} aria-hidden="true">
            {expanded ? <ChevronDown20Regular /> : <ChevronRight20Regular />}
          </span>
          <span className={styles.summaryName}>
            <span
              className={
                row.name.trim() ? undefined : styles.summaryNamePlaceholder
              }
            >
              {displayName}
            </span>
            {nameError && (
              <span
                className={styles.summaryError}
                role="img"
                aria-label={nameError}
                title={nameError}
              >
                <ErrorCircle16Regular />
              </span>
            )}
          </span>
          <Badge appearance="outline" size="small">
            {labelOf(row.type)}
          </Badge>
        </button>
        <Tooltip content="Remove parameter" relationship="label">
          <Button
            appearance="subtle"
            icon={<DeleteRegular />}
            aria-label={`Remove parameter ${displayName}`}
            onClick={() => onRemove(row.id)}
            disabled={readOnly}
          />
        </Tooltip>
      </div>
      {expanded && (
        <div id={bodyId} className={styles.cardBody}>
          <div className={styles.headerRow}>
            <Field label="Name" required {...validationProps(nameError)}>
              <Input
                value={row.name}
                onChange={(_, data) => onUpdate(row.id, { name: data.value })}
                disabled={readOnly}
                placeholder="parameterName"
              />
            </Field>
            <Field label="Type" className={styles.fullField}>
              <Dropdown
                className={styles.typeDropdown}
                value={labelOf(row.type)}
                selectedOptions={[row.type]}
                onOptionSelect={(_, data) =>
                  onUpdate(row.id, {
                    type: (data.optionValue ?? "String") as ParameterType,
                  })
                }
                disabled={readOnly}
              >
                {PARAMETER_TYPES.map((t) => (
                  <Option key={t} value={t} text={labelOf(t)}>
                    {labelOf(t)}
                  </Option>
                ))}
              </Dropdown>
            </Field>
          </div>
          <Field label="Description">
            <Input
              value={row.description}
              onChange={(_, data) =>
                onUpdate(row.id, { description: data.value })
              }
              disabled={readOnly}
              placeholder="Optional description"
            />
          </Field>
          <ValueField
            type={row.type}
            value={row.rawValue}
            disabled={readOnly}
            onChange={(v) => onUpdate(row.id, { rawValue: v })}
          />
        </div>
      )}
    </div>
  );
};

ParameterCard.displayName = "ParameterCard";

interface ValueFieldProps {
  type: ParameterType;
  value: string;
  disabled: boolean;
  onChange: (next: string) => void;
}

const ValueField = ({ type, value, disabled, onChange }: ValueFieldProps) => {
  const styles = useStyles();
  const errorProps = validationProps(validateValue(type, value));

  if (type === "Bool") {
    // Bool values can be backed by an expression (e.g. `@appsetting('Foo')`)
    // that the model preserves verbatim. Forcing those through a Switch would
    // misrender as `false` and silently overwrite the expression on toggle —
    // fall back to a text input so the raw value stays editable.
    const isLiteral = value === "" || value === "true" || value === "false";
    if (!isLiteral) {
      return (
        <Field
          label="Value"
          hint="Expression — clear to use the true/false toggle."
          {...errorProps}
        >
          <Input
            value={value}
            onChange={(_, data) => onChange(data.value)}
            disabled={disabled}
          />
        </Field>
      );
    }
    const checked = value === "true";
    return (
      <Field label="Value" {...errorProps}>
        <Switch
          checked={checked}
          onChange={(_, data) => onChange(data.checked ? "true" : "false")}
          label={checked ? "true" : "false"}
          disabled={disabled}
        />
      </Field>
    );
  }

  if (type === "Array" || type === "Object") {
    return (
      <Field
        label="Value"
        hint={type === "Array" ? "Enter a JSON array." : "Enter a JSON object."}
        {...errorProps}
      >
        <Textarea
          className={styles.jsonTextarea}
          value={value}
          onChange={(_, data) => onChange(data.value)}
          disabled={disabled}
          rows={4}
          placeholder={type === "Array" ? "[1, 2, 3]" : '{ "key": "value" }'}
        />
      </Field>
    );
  }

  if (type === "Int" || type === "Float") {
    return (
      <Field label="Value" {...errorProps}>
        <Input
          inputMode={type === "Int" ? "numeric" : "decimal"}
          value={value}
          onChange={(_, data) => onChange(data.value)}
          disabled={disabled}
          placeholder={type === "Int" ? "42" : "3.14"}
        />
      </Field>
    );
  }

  // String — also accepts expressions like `@appsetting('Foo')`.
  return (
    <Field label="Value" {...errorProps}>
      <Input
        value={value}
        onChange={(_, data) => onChange(data.value)}
        disabled={disabled}
        placeholder="Enter value"
      />
    </Field>
  );
};

ValueField.displayName = "ValueField";

const useStyles = makeStyles({
  root: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    minHeight: 0,
    overflow: "auto",
    paddingRight: tokens.spacingHorizontalXS,
  },
  list: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalM,
  },
  card: {
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: tokens.colorNeutralBackground1,
    display: "flex",
    flexDirection: "column",
  },
  cardExpanded: {
    boxShadow: tokens.shadow2,
  },
  summaryRow: {
    display: "flex",
    alignItems: "center",
    gap: tokens.spacingHorizontalXS,
    paddingInline: tokens.spacingHorizontalXS,
  },
  summary: {
    flex: 1,
    minWidth: 0,
    display: "grid",
    gridTemplateColumns: "auto minmax(0, 1fr) auto",
    alignItems: "center",
    gap: tokens.spacingHorizontalS,
    paddingBlock: tokens.spacingVerticalS,
    paddingInline: tokens.spacingHorizontalS,
    background: "none",
    border: "none",
    cursor: "pointer",
    textAlign: "left",
    color: "inherit",
    font: "inherit",
    borderRadius: tokens.borderRadiusMedium,
    ":hover": {
      backgroundColor: tokens.colorNeutralBackground1Hover,
    },
    ":focus-visible": {
      outline: `2px solid ${tokens.colorStrokeFocus2}`,
      outlineOffset: "-2px",
    },
  },
  summaryChevron: {
    color: tokens.colorNeutralForeground2,
    display: "flex",
    alignItems: "center",
  },
  summaryName: {
    fontWeight: tokens.fontWeightSemibold,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    display: "flex",
    alignItems: "center",
    gap: tokens.spacingHorizontalXS,
  },
  summaryNamePlaceholder: {
    color: tokens.colorNeutralForeground3,
    fontStyle: "italic",
    fontWeight: tokens.fontWeightRegular,
  },
  summaryError: {
    color: tokens.colorPaletteRedForeground1,
    display: "flex",
    alignItems: "center",
  },
  cardBody: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalS,
    paddingInline: tokens.spacingHorizontalM,
    paddingBlock: tokens.spacingVerticalM,
    borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  headerRow: {
    display: "grid",
    gridTemplateColumns: "minmax(0, 1fr) minmax(0, 200px)",
    gap: tokens.spacingHorizontalS,
    alignItems: "end",
  },
  // Grid items default to `min-width: auto`; without these the Dropdown's
  // intrinsic width pushes the type column past its track.
  fullField: {
    width: "100%",
    minWidth: 0,
  },
  typeDropdown: {
    width: "100%",
    minWidth: 0,
  },
  jsonTextarea: {
    width: "100%",
    "& textarea": {
      fontFamily: tokens.fontFamilyMonospace,
      fontSize: tokens.fontSizeBase200,
      minHeight: "80px",
    },
  },
  empty: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: tokens.spacingVerticalS,
    padding: tokens.spacingVerticalXXL,
    color: tokens.colorNeutralForeground2,
    border: `1px dashed ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusMedium,
  },
  toolbar: {
    display: "flex",
    justifyContent: "flex-start",
    marginTop: tokens.spacingVerticalM,
  },
  banner: {
    padding: tokens.spacingVerticalS,
    paddingInline: tokens.spacingHorizontalM,
    border: `1px solid ${tokens.colorPaletteYellowBorder1}`,
    backgroundColor: tokens.colorPaletteYellowBackground1,
    color: tokens.colorPaletteYellowForeground1,
    borderRadius: tokens.borderRadiusMedium,
    marginBottom: tokens.spacingVerticalM,
    fontSize: tokens.fontSizeBase200,
  },
});
